import React, { FC } from "react";

import { useIntl } from "react-intl";

import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import User from "@mapmycustomers/shared/types/User";

import ViewType from "@app/scene/dashboard/enum/ViewType";
import PerformanceData from "@app/scene/dashboard/types/PerformanceData";
import { isOwner } from "@app/store/iam/util";
import Scope from "@app/types/dashboard/Scope";

import { Sorters } from "../utils/useSorters";

import CollapsibleScopeItem from "./CollapsibleScopeItem";
import MetricValue from "./MetricValue";
import ScopeItem from "./ScopeItem";
import UserLabel from "./UserLabel";

export interface OwnersProps {
  activeScope: Scope;
  currentUser: User;
  owners: User[];
  performanceData: PerformanceData;
  performanceDataLoading?: boolean;
  rankingMetric: IField;
  sorter: Sorters["userSorter"];
  view: ViewType;
}

const Owners: FC<OwnersProps> = ({
  activeScope,
  currentUser,
  owners,
  performanceData,
  performanceDataLoading,
  rankingMetric,
  sorter,
  view,
}) => {
  const intl = useIntl();

  if (!isOwner(currentUser)) {
    return null;
  }

  const ownerItems = sorter(owners).map((owner) => (
    <ScopeItem
      isOwner
      key={owner.id}
      label={<UserLabel performance={performanceData[`undefined_${owner.id}`]} user={owner} />}
      selected={!activeScope.teamId && activeScope.userId === owner.id}
      suffix={
        <MetricValue
          loading={performanceDataLoading}
          metric={rankingMetric}
          performance={performanceData[`undefined_${owner.id}`]}
          userIds={[owner.id]}
        />
      }
      topLevel={owners.length <= 3}
      userId={owner.id}
      view={view}
    />
  ));

  return owners.length > 3 && ownerItems ? (
    <CollapsibleScopeItem
      clickable={false}
      defaultCollapsed
      key="owners"
      label={intl.formatMessage({
        id: "dashboard.sidebar.owners",
        defaultMessage: "All Owners",
        description: "Owners section in Dashboard sidebar when org has more than 3 owners",
      })}
      view={view}
    >
      {ownerItems}
    </CollapsibleScopeItem>
  ) : (
    <>{ownerItems}</>
  );
};

export default Owners;
